import { SpinnerDotted } from "spinners-react";
import VCard from "vcard-creator";
import fileDownload from "js-file-download";
import { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { appDb } from "../config";
import { doc, getDoc } from "firebase/firestore";
import { Image } from "../components/Image";
import { QRNormal } from "react-qrbtf";
import Icon from "./../assets/img/logo.png";
import ReactCardFlip from "react-card-flip";
import { Brand } from "./brand";
import { Dialog, Transition } from "@headlessui/react";
import Sheet from "react-modal-sheet";
import CompanyProfile from "../components/org/company-profile";
import { createClient } from "@supabase/supabase-js";
import { browserName, deviceType } from "react-device-detect";
import AndroidInstructions from "../components/AndroidInstructions";
import { isAndroid } from "../utils/deviceUtils";

// Create a single supabase client for interacting with your database
const supabase = createClient(
  "https://pppxbumchydernriwxyu.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBwcHhidW1jaHlkZXJucml3eHl1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzAyOTgxMTIsImV4cCI6MjA0NTg3NDExMn0.5WiZCJJh8aolfx_TgJiu-z8Ok3qdh2lEjN4mLydMHJ4"
);

function Company() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  let { id } = useParams();
  const [showBack, setShowBack] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [installOpened, setInstallOpened] = useState(false);

  const [isFooterVisible, setIsFooterVisible] = useState(true);

  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          if (currentScrollY > lastScrollY.current + 10) {
            setIsFooterVisible(false);
          } else if (currentScrollY < lastScrollY.current - 10) {
            setIsFooterVisible(true);
          }
          lastScrollY.current = currentScrollY > 0 ? currentScrollY : 0;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (id) {
      getData(id);
      saveEvent({ action_type: "view_code", target: "page" });
    }
  }, [id]);

  const getData = async (forId) => {
    setLoading(true);
    const docRef = doc(appDb, "orgs", forId);
    await getDoc(docRef)
      .then(async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setData(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const save = (userInfo) => {
    saveEvent({ action_type: "save", target: "save_btn" });
    const card = new VCard();
    if (data !== null) {
      card.setFormat("vcard");
      
      // For companies, use the brand name as the main name
      card.addName(data.brand.name, '', '', '', '');
      card.addLogoURL(data.brand.logo);
      card.addCompany(data.brand.name);
      
      // Add website URL properly
      if (data.brand.website) {
        card.addURL(data.brand.website);
      }
      
      card.addPhotoURL(data.brand.wall);
      
      // Add address as full text in street field
      if (data.brand.address) {
        card.addAddress('', '', data.brand.address, '', '', '', '');
      }
      
      card.setFilename(data.brand.name);

      if (data.contact_info) {
        for (var i = 0; i < data.contact_info.length; i++) {
          const info = data.contact_info[i];
          if (info.type === "email") {
            card.addEmail(info.value);
          } else if (info.type === "phone") {
            card.addPhoneNumber(info.value, "WORK");
          } else if (info.type === "link") {
            // Check if it's a WhatsApp link
            if (info.value && (info.value.includes('wa.me/') || info.value.includes('whatsapp.com'))) {
              // Extract phone number from WhatsApp URL - get everything after wa.me/
              const phoneMatch = info.value.match(/wa\.me\/(.+)/);
              if (phoneMatch) {
                const phoneNumber = phoneMatch[1];
                card.addPhoneNumber(phoneNumber, "WORK");
              }
            } else if (info.value) {
              card.addURL(info.value);
            }
          }
        }
      }
      
      // Add social links if available (just URL, no platform/handle)
      if (data.links && data.links.social) {
        data.links.social.forEach((socialUrl) => {
          if (socialUrl) {
            card.addURL(socialUrl);
          }
        });
      }
      
      // Add other URLs
      if (data.links && data.links.other) {
        data.links.other.forEach((link) => {
          if (link.url) {
            card.addURL(link.url);
          }
        });
      }

      fileDownload(card.toString(), `${data.brand.name}.vcf`, isAndroid() ? "text/x-vcard" : "text/vcard");
      setInstallOpened(true);
    } else {
      // TODO:
    }
  };

  const getUrl = () => {
    const cannonical = document.querySelector("link[rel=cannonical]");
    const url = cannonical ? cannonical.href : document.location.href;
    return url;
  };
  const share = async () => {
    saveEvent({ action_type: "share", target: "share_btn" });
    const shareDetails = {
      url: `${getUrl()}`,
      title: "Digital business card",
      text: `Hello there, here is ${data.brand.name} details.`,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareDetails).then(() => {
          setInstallOpened(true);
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const saveEvent = async (event) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { error } = await supabase.from("codes_analytics").insert({
      code_id: id,
      action_type: event.action_type,
      target: event.target,
      browser: browserName,
      device_type: deviceType,
      location: timeZone,
    });
    if (error) {
      console.error(error);
    }
  };

  if (loading)
    return (
      <div className="flex w-screen h-screen">
        <div className="m-auto">
          <SpinnerDotted
            size={50}
            thickness={100}
            speed={100}
            color="#36ad47"
          />
        </div>
      </div>
    );

  if (!data) return <Brand />;

  const makeCode = (small) => (
    <QRNormal
      value={getUrl()}
      className="my-qrcode"
      styles={{ svg: { width: small ? `160px` : "320px" } }}
      type="round"
      size={72}
      opacity={100}
      posType="round"
      otherColor="#808080"
      posColor="#000000"
      icon={Icon}
    />
  );

  return (
    <>
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <div className="mb-64 p-1">
          {data && (
            <CompanyProfile
              onEvent={saveEvent}
              {...{
                company: data.brand.name,
                tagline: data.brand.tag,
                biography: data.brand.bio,
                website: data.brand.website,
                address: data.brand.address,
                wallpaperImage: data.brand.wall,
                logoImage: data.brand.logo,
                links: data.links,
                contactInfo: data.contact_info,
                accentColor: data.theme.accent,
                backgroundColor: data.theme.background,
                primaryColor: data.theme.primary,
                secondaryColor: data.theme.secondary,
              }}
            />
          )}
        </div>
        <div className="grow flex" />
        <footer
          style={{
            "--accent-color": data ? data.theme.accent : "slate-700",
            "--background-color": "white",
            "--primary-color": data ? data.theme.primary : "slate-900",
            "--secondary-color": data ? data.theme.secondary : "slate-800",
            backgroundColor: "var(--background-color)",
          }}
          className={`fixed bottom-0 left-0 right-0 flex md:items-center md:justify-between mb-0 bg-white ${
            data ? `border-[${data.theme.accent}]` : "border-slate-300"
          } border-t transition-transform duration-300 ease-in-out`}
        >
          <div className="w-full flex flex-col">
            {/* {data && (
              <div class="flex gap-4 justify-center w-full max-w-md mx-auto pr-4 pl-4">
                <button
                  type="button"
                  className="text-slate-700 bg-white hover:text-slate-700 hover:bg-gray-100 border border-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center justify-center gap-2"
                  onClick={share}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  style={{
                    borderColor: "var(--accent-color)",
                    borderWidth: "2px",
                    color: "var(--primary-color)",
                  }}
                  className={` hover:text-slate-700 hover:bg-gray-100 border border-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center flex-1 justify-center gap-2`}
                  onClick={() => save(data)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
                    />
                  </svg>
                  Save company contacts
                </button>
              </div>
            )} */}
            <div className="w-full p-4 flex justify-center items-center gap-2">
              <img src={Icon} alt="Nami" className="h-4 w-4 object-contain" />
              <span className="text-xs text-gray-500 sm:text-center dark:text-gray-400 ">
                Runs on{" "}
                <span className="font-semibold text-sm">
                  <a
                    href="https://nami.africa"
                    className="hover:underline"
                  >
                    Nami
                  </a>
                </span>
              </span>
            </div>
          </div>
        </footer>
      </div>
      <Transition appear show={showCard} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10 "
          onClose={() => setShowCard(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto rotate-90">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl text-left align-middle shadow-xl transition-all">
                  <ReactCardFlip
                    isFlipped={showBack}
                    flipDirection="vertical"
                    flipSpeedBackToFront={0.6}
                    flipSpeedFrontToBack={0.6}
                  >
                    <div
                      className="p-0 justify-center items-center max-w-screen-sm"
                      onClick={() => setShowBack(true)}
                    >
                      {data.business && data.business.card_front && (
                        <Image src={data.business.card_front} />
                      )}
                    </div>
                    <div
                      className="justify-center items-center transition-all transition ease-in-out "
                      onClick={() => setShowBack(false)}
                    >
                      {data.business && data.business.card_back && (
                        <Image src={data.business.card_back} />
                      )}
                    </div>
                  </ReactCardFlip>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      
      <Sheet
        isOpen={installOpened}
        onClose={() => setInstallOpened(false)}
        disableDrag={true}
      >
        <Sheet.Container>
          <Sheet.Header>
            <div className="flex justify-end p-2">
              <button
                className="p-2"
                onClick={() => setInstallOpened(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="gray"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </Sheet.Header>
          <Sheet.Content>
            {isAndroid() ? (
              <AndroidInstructions onClose={() => setInstallOpened(false)} />
            ) : (
              <Brand />
            )}
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
    </>
  );
}

export default Company;
